import React from 'react'
import {
  Box,
  SimpleGrid,
  Icon,
  Text,
  Stack,
  Flex,
  useColorModeValue,
  Container,
  Heading
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  FaRobot,
  FaChartLine,
  FaShieldAlt,
  FaBolt,
  FaBrain,
  FaClock
} from 'react-icons/fa'

const MotionBox = motion(Box)

interface FeatureProps {
  title: string
  text: string
  icon: React.ElementType
  delay: number
}

const Feature = ({ title, text, icon, delay }: FeatureProps) => {
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      bg="dark.card"
      p={8}
      borderRadius="2xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      position="relative"
      overflow="hidden"
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: '0 20px 40px -15px rgba(59, 130, 246, 0.3)',
        borderColor: 'brand.500'
      }}
      transitionProperty="transform, box-shadow, border-color"
      transitionDuration="0.3s"
    >
      {/* Background Glow */}
      <Box
        position="absolute"
        top="-50%"
        right="-50%"
        w="100%"
        h="100%"
        bgGradient="radial(brand.500 0%, transparent 70%)"
        opacity={0.1}
        filter="blur(40px)"
      />

      <Stack spacing={4} position="relative" zIndex={1}>
        <Flex
          w={12}
          h={12}
          align="center"
          justify="center"
          color="white"
          rounded="xl"
          bgGradient="linear(to-br, brand.400, brand.600)"
          mb={2}
        >
          <Icon as={icon} w={6} h={6} />
        </Flex>
        <Text fontWeight="bold" fontSize="xl" color="white">
          {title}
        </Text>
        <Text color="gray.400" fontSize="md" lineHeight="relaxed">
          {text}
        </Text>
      </Stack>
    </MotionBox>
  )
}

const FeatureSection = () => {
  const { t } = useTranslation()

  const features = [
    {
      title: t('AI 딥러닝 분석'),
      text: t('과거 데이터를 학습한 AI가 시장의 패턴을 분석하여 최적의 매매 타이밍을 포착합니다.'),
      icon: FaBrain,
    },
    {
      title: t('24시간 자동매매'),
      text: t('잠든 사이에도 멈추지 않습니다. 365일 24시간 시장을 감시하고 기회를 놓치지 않습니다.'),
      icon: FaClock,
    },
    {
      title: t('실시간 리스크 관리'),
      text: t('급격한 변동성에도 안전합니다. AI가 실시간으로 위험을 감지하고 방어적인 포지션을 취합니다.'),
      icon: FaShieldAlt,
    },
    {
      title: t('초고속 주문 실행'),
      text: t('0.1초 이내의 빠른 주문 실행으로 슬리피지를 최소화하고 수익을 극대화합니다.'),
      icon: FaBolt,
    },
    {
      title: t('빅데이터 전략'),
      text: t('수년간의 검증된 데이터를 기반으로 한 전략으로 안정적인 수익을 추구합니다.'),
      icon: FaChartLine,
    },
    {
      title: t('감정 없는 트레이딩'),
      text: t('공포와 탐욕을 배제하고 철저하게 데이터와 알고리즘에 기반한 매매를 수행합니다.'),
      icon: FaRobot,
    },
  ]

  return (
    <Box py={{ base: 20, md: 24 }} bg="dark.bg">
      <Container maxW="7xl">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {features.map((feature, index) => (
            <Feature
              key={index}
              icon={feature.icon}
              title={feature.title}
              text={feature.text}
              delay={index * 0.1}
            />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  )
}

export default FeatureSection
